import { useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  PRINTER_CONNECTIONS,
  type CreatePrinterInput,
  type PrinterConnection,
  type PrinterDto,
  type UpdatePrinterInput,
} from '@gc/shared';

import { api } from '@/lib/api';
import { Toggle, ghostPillClass, inputClass, pillClass } from './controls';
import { toFieldErrors, type FieldError } from './useSettings';

/** What the dialog's fields hold, as typed. */
export interface PrinterForm {
  name: string;
  connection: PrinterConnection;
  host: string;
  port: string;
  windowsPrinterName: string;
  isActive: boolean;
}

export function printerFormOf(printer: PrinterDto | null): PrinterForm {
  return {
    name: printer?.name ?? '',
    connection: printer?.connection ?? 'TCP',
    host: printer?.host ?? '',
    port: String(printer?.port ?? 9100),
    windowsPrinterName: printer?.windowsPrinterName ?? '',
    isActive: printer?.isActive ?? true,
  };
}

/**
 * The body for both Add and Save. Only the half that matches the connection is
 * sent; the other half goes as null, which is what the server stores for it
 * anyway. The whole row is sent on an edit too — the server judges "a TCP
 * printer needs a host, a USB one needs a name" on the merged row, and
 * answers with the field to fix.
 */
export function printerPayload(form: PrinterForm): CreatePrinterInput & UpdatePrinterInput {
  const tcp = form.connection === 'TCP';
  return {
    name: form.name,
    connection: form.connection,
    host: tcp ? form.host : null,
    port: tcp ? Number(form.port) || 9100 : null,
    windowsPrinterName: tcp ? null : form.windowsPrinterName,
    isActive: form.isActive,
  };
}

/**
 * Add or edit a named printer (SPEC §6.1) — SHISHA, BAR, CASH, KITCHEN.
 *
 * TCP asks for host:port; USB asks for the exact Windows printer name on the
 * main PC. Only the relevant half is shown, because the server stores only the
 * half that matches the connection kind.
 *
 * EDIT exists because a printer whose address changed (a new router, DHCP)
 * could otherwise only be deleted and re-added — which the server refuses
 * while any category routes to it, so in practice it could not be fixed.
 *
 * PORTALLED TO <body>, AND IT HAS TO BE. The General Settings page is itself
 * one big <form>, and this dialog carries its own. A <form> inside a <form> is
 * invalid HTML: React refuses to nest them, the inner one is neutered, and
 * pressing Add fires NOTHING — no request, no error, no clue. The venue hit
 * exactly that: printers could not be added at all, silently.
 */
export function PrinterDialog({
  printer = null,
  onClose,
  onCreated,
}: {
  /** The printer to edit; absent to add a new one. */
  printer?: PrinterDto | null;
  onClose: () => void;
  /** Called after a successful add or save. */
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = printer !== null;

  const [form, setForm] = useState<PrinterForm>(() => printerFormOf(printer));
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const set = <K extends keyof PrinterForm>(key: K, value: PrinterForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const errorFor = (field: string) => errors.find((issue) => issue.field === field)?.message;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrors([]);
    setSubmitting(true);

    try {
      if (isEdit) {
        await api.patch(`/settings/printers/${printer.id}`, printerPayload(form));
      } else {
        await api.post('/settings/printers', printerPayload(form));
      }
      onCreated();
      onClose();
    } catch (caught) {
      setErrors(
        toFieldErrors(
          caught,
          isEdit ? t('settings.general.printerSaveFailed') : t('settings.general.printerAddFailed'),
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="max-h-full w-full max-w-md overflow-auto rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold text-content">
          {isEdit ? t('settings.general.editPrinter') : t('settings.general.addPrinter')}
        </h2>

        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">{t('common.name')}</span>
            <input
              value={form.name}
              onChange={(event) => set('name', event.target.value)}
              required
              maxLength={40}
              placeholder="CASH"
              className={inputClass}
            />
            {errorFor('name') ? (
              <p className="mt-1 text-xs text-danger">{errorFor('name')}</p>
            ) : null}
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('settings.general.printerConnection')}
            </span>
            <select
              value={form.connection}
              onChange={(event) => set('connection', event.target.value as PrinterConnection)}
              className={inputClass}
            >
              {PRINTER_CONNECTIONS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`settings.general.printerConnectionKind.${kind}`)}
                </option>
              ))}
            </select>
          </label>

          {form.connection === 'TCP' ? (
            <div className="grid grid-cols-3 gap-3">
              <label className="col-span-2 block">
                <span className="mb-1 block text-sm text-content-secondary">
                  {t('settings.general.printerHost')}
                </span>
                <input
                  value={form.host}
                  onChange={(event) => set('host', event.target.value)}
                  placeholder="192.168.1.50"
                  className={inputClass}
                />
                {errorFor('host') ? (
                  <p className="mt-1 text-xs text-danger">{errorFor('host')}</p>
                ) : null}
              </label>
              <label className="block">
                <span className="mb-1 block text-sm text-content-secondary">
                  {t('settings.general.printerPort')}
                </span>
                <input
                  type="number"
                  min={1}
                  max={65535}
                  value={form.port}
                  onChange={(event) => set('port', event.target.value)}
                  className={`${inputClass} tabular`}
                />
                {errorFor('port') ? (
                  <p className="mt-1 text-xs text-danger">{errorFor('port')}</p>
                ) : null}
              </label>
            </div>
          ) : (
            <label className="block">
              <span className="mb-1 block text-sm text-content-secondary">
                {t('settings.general.windowsPrinterName')}
              </span>
              <input
                value={form.windowsPrinterName}
                onChange={(event) => set('windowsPrinterName', event.target.value)}
                className={inputClass}
              />
              {errorFor('windowsPrinterName') ? (
                <p className="mt-1 text-xs text-danger">{errorFor('windowsPrinterName')}</p>
              ) : null}
            </label>
          )}

          <Toggle
            label={t('settings.general.printerActive')}
            hint={t('settings.general.printerActiveHint')}
            checked={form.isActive}
            onChange={(value) => set('isActive', value)}
          />
        </div>

        {errorFor('(root)') ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {errorFor('(root)')}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={ghostPillClass}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={submitting} className={pillClass}>
            {isEdit ? t('common.save') : t('common.add')}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
